import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Database, Eye, Lock, Users, Globe } from 'lucide-react';

const PrivacyPolicy = () => {
  const lastUpdated = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long', 
    day: 'numeric'
  });

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <Link 
              to="/register" 
              className="flex items-center text-blue-600 hover:text-blue-700 transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Registration
            </Link>
            <div className="flex items-center text-gray-500">
              <Shield className="h-5 w-5 mr-2" />
              <span className="text-sm">Privacy Document</span>
            </div>
          </div>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Privacy Policy
          </h1>
          <p className="text-gray-600">
            How we collect, use, and protect your personal information
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Last updated: {lastUpdated}
          </p>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <div className="prose max-w-none">
            
            {/* Introduction */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <Shield className="h-5 w-5 mr-2 text-green-600" />
                Our Commitment to Privacy
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We are committed to protecting your privacy and ensuring the security of your personal 
                information. This Privacy Policy explains how we collect, use, disclose, and safeguard 
                your information when you use our Incident Management System.
              </p>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-green-800 text-sm">
                  <strong>Key Principle:</strong> We only collect information necessary to provide and 
                  improve our services. We never sell your personal data to third parties.
                </p>
              </div>
            </section>

            {/* Information We Collect */}
            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <Database className="h-5 w-5 mr-2 text-blue-600" />
                1. Information We Collect
              </h3>
              
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Account Information</h4>
                  <ul className="space-y-1 text-gray-700 ml-4">
                    <li>• Name and email address</li>
                    <li>• Username and encrypted password</li>
                    <li>• Phone number (optional)</li>
                    <li>• Company and department information (optional)</li>
                    <li>• Profile picture (optional)</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Incident Data</h4>
                  <ul className="space-y-1 text-gray-700 ml-4">
                    <li>• Incident reports and descriptions</li>
                    <li>• Comments and communications</li>
                    <li>• File attachments and screenshots</li>
                    <li>• Timestamps and status updates</li>
                    <li>• Assignment and resolution data</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Usage Information</h4>
                  <ul className="space-y-1 text-gray-700 ml-4">
                    <li>• Login times and session duration</li>
                    <li>• IP addresses and browser information</li>
                    <li>• Feature usage and navigation patterns</li>
                    <li>• System performance and error logs</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* How We Use Information */}
            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <Eye className="h-5 w-5 mr-2 text-purple-600" />
                2. How We Use Your Information
              </h3>
              
              <div className="space-y-4 text-gray-700">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Service Provision</h4>
                  <ul className="space-y-1 ml-4">
                    <li>• Creating and managing your user account</li>
                    <li>• Processing and tracking incident reports</li>
                    <li>• Facilitating team collaboration and communication</li>
                    <li>• Providing notifications and updates</li>
                    <li>• Generating reports and analytics</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Service Improvement</h4>
                  <ul className="space-y-1 ml-4">
                    <li>• Analyzing usage patterns to improve features</li>
                    <li>• Identifying and fixing technical issues</li>
                    <li>• Developing new functionality</li>
                    <li>• Optimizing system performance</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Communication</h4>
                  <ul className="space-y-1 ml-4">
                    <li>• Sending service-related notifications</li>
                    <li>• Providing customer support</li>
                    <li>• Sharing important system updates</li>
                    <li>• Marketing communications (with consent)</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Data Security */}
            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <Lock className="h-5 w-5 mr-2 text-red-600" />
                3. Data Security & Protection
              </h3>
              
              <div className="space-y-4 text-gray-700">
                <p>We implement multiple layers of security to protect your information:</p>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-medium text-blue-900 mb-2">Technical Safeguards</h4>
                    <ul className="space-y-1 text-blue-800 text-sm">
                      <li>• End-to-end encryption for data transmission</li>
                      <li>• Encrypted database storage</li>
                      <li>• Secure authentication systems</li>
                      <li>• Regular security audits and updates</li>
                    </ul>
                  </div>

                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <h4 className="font-medium text-purple-900 mb-2">Access Controls</h4>
                    <ul className="space-y-1 text-purple-800 text-sm">
                      <li>• Role-based permission systems</li>
                      <li>• Multi-factor authentication options</li>
                      <li>• Session management and timeouts</li>
                      <li>• Audit logs for all data access</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="font-medium text-red-900 mb-2">Physical & Operational Security</h4>
                  <ul className="space-y-1 text-red-800 text-sm">
                    <li>• Secure data centers with 24/7 monitoring</li>
                    <li>• Background-checked staff with limited access</li>
                    <li>• Regular backups and disaster recovery procedures</li>
                    <li>• Incident response and breach notification procedures</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Data Sharing */}
            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <Users className="h-5 w-5 mr-2 text-orange-600" />
                4. Information Sharing & Disclosure
              </h3>
              
              <div className="space-y-4 text-gray-700">
                <p className="font-medium">We do not sell, trade, or rent your personal information to third parties.</p>
                
                <p>We may share your information only in these limited circumstances:</p>
                <ul className="space-y-2 ml-4">
                  <li>• <strong>Within your organization:</strong> With colleagues who need access for incident management</li>
                  <li>• <strong>Service providers:</strong> With trusted vendors who help us operate our service (under strict confidentiality agreements)</li>
                  <li>• <strong>Legal requirements:</strong> When required by law, court order, or government regulation</li>
                  <li>• <strong>Business transfers:</strong> In the event of a merger, acquisition, or sale of assets (with user notification)</li>
                  <li>• <strong>Safety purposes:</strong> To protect the rights, property, or safety of our users or others</li>
                </ul>
              </div>
            </section>

            {/* Your Rights */}
            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <Globe className="h-5 w-5 mr-2 text-indigo-600" />
                5. Your Privacy Rights
              </h3>
              
              <div className="space-y-4 text-gray-700">
                <p>You have the following rights regarding your personal information:</p>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Access & Control</h4>
                    <ul className="space-y-1 text-sm">
                      <li>• View and download your personal data</li>
                      <li>• Update or correct your information</li>
                      <li>• Delete your account and data</li>
                      <li>• Export your data in a portable format</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Communication Preferences</h4>
                    <ul className="space-y-1 text-sm">
                      <li>• Opt out of marketing communications</li>
                      <li>• Choose notification preferences</li>
                      <li>• Manage email subscription settings</li>
                      <li>• Request information about data processing</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                  <p className="text-indigo-800 text-sm">
                    <strong>How to Exercise Your Rights:</strong> Contact us at privacy@yourcompany.com 
                    or use the privacy controls in your account settings. We will respond to your request 
                    within 30 days.
                  </p>
                </div>
              </div>
            </section>

            {/* Data Retention */}
            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">6. Data Retention</h3>
              <div className="space-y-3 text-gray-700">
                <p>We retain your information for as long as necessary to provide our services:</p>
                <ul className="space-y-2 ml-4">
                  <li>• <strong>Account data:</strong> Until you delete your account or request deletion</li>
                  <li>• <strong>Incident data:</strong> For 7 years or as required by your organization's policies</li>
                  <li>• <strong>Usage logs:</strong> For 2 years for security and analytics purposes</li>
                  <li>• <strong>Legal holds:</strong> Longer if required by law or legal proceedings</li>
                </ul>
                <p>
                  When data is deleted, it is securely removed from our active systems and backups 
                  within 90 days, except where longer retention is required by law.
                </p>
              </div>
            </section>

            {/* Cookies and Tracking */}
            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">7. Cookies & Tracking</h3>
              <div className="space-y-3 text-gray-700">
                <p>We use cookies and similar technologies to:</p>
                <ul className="space-y-2 ml-4">
                  <li>• Keep you signed in to your account</li>
                  <li>• Remember your preferences and settings</li>
                  <li>• Analyze how our service is used</li>
                  <li>• Improve security and prevent fraud</li>
                </ul>
                <p>
                  You can control cookie settings through your browser, but some features may not 
                  work properly if cookies are disabled.
                </p>
              </div>
            </section>

            {/* International Transfers */}
            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">8. International Data Transfers</h3>
              <div className="space-y-3 text-gray-700">
                <p>
                  Your information may be transferred to and processed in countries other than your 
                  own. We ensure that such transfers are protected by appropriate safeguards, including 
                  standard contractual clauses approved by relevant authorities.
                </p>
              </div>
            </section>

            {/* Children's Privacy */}
            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">9. Children's Privacy</h3>
              <div className="space-y-3 text-gray-700">
                <p>
                  Our service is not intended for individuals under the age of 18. We do not knowingly 
                  collect personal information from children. If we become aware that we have collected 
                  information from a child, we will take steps to delete it promptly.
                </p>
              </div>
            </section>

            {/* Changes to Privacy Policy */}
            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">10. Changes to This Policy</h3>
              <div className="space-y-3 text-gray-700">
                <p>
                  We may update this Privacy Policy periodically. When we make significant changes, 
                  we will notify you via email or through a prominent notice in our service. The 
                  "Last Updated" date at the top of this policy indicates when the most recent changes 
                  were made.
                </p>
              </div>
            </section>

            {/* Contact Information */}
            <section className="mb-8 pb-8 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">11. Contact Us</h3>
              <div className="space-y-3 text-gray-700">
                <p>If you have questions about this Privacy Policy or our privacy practices:</p>
                <ul className="space-y-2 ml-4">
                  <li>• Email: privacy@yourcompany.com</li>
                  <li>• Support: support@yourcompany.com</li>
                  <li>• Phone: +1 (555) 123-4567</li>
                  <li>• Mail: Privacy Officer, 123 Business St, City, State 12345</li>
                </ul>
              </div>
            </section>

            {/* Footer */}
            <div className="text-center pt-6">
              <p className="text-gray-500 text-sm mb-4">
                By using our service, you acknowledge that you have read and understood this Privacy Policy.
              </p>
              <div className="flex justify-center space-x-4">
                <Link 
                  to="/register" 
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
                >
                  Back to Registration
                </Link>
                <Link 
                  to="/terms-of-service" 
                  className="text-blue-600 hover:text-blue-700 px-6 py-2 transition-colors"
                >
                  Terms of Service
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;